import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDocumentPreviewUrl } from '@/features/documents/hooks/use-document-preview-url';
import { useDownloadDocument } from '@/features/documents/hooks/use-download-document';
import { FileType } from '@/features/documents/util/mimetype-util';
import { useCurrentOrg } from '@/features/org/hooks/use-org';
import OneItemHighlighter from '@/shared/components/card-elements/one-item-highlighter';
import { RoundedDropdownMenu } from '@/shared/components/rounded-dropdown-menu';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/shared/components/ui/card';
import { DropdownMenuItem } from '@/shared/components/ui/dropdown';
import { DocumentVisibility } from '@/shared/components/ui/upload/document-upload-dialog';
import VisbilityIndicator from '@/shared/components/visibility-indicator';
import { toast } from '@/shared/hooks/use-toast';
import { ProjectType, SecureProjectDocument } from '@/shared/types/api';
import { getInitials } from '@/shared/utils/avatar-util';
import { formatDate } from '@/shared/utils/format';

export interface DocumentCardProps {
  document: SecureProjectDocument;
  onEdit: () => void;
  onDelete: () => void;
  hasWriteAccess: boolean;
  viewingProjectType?: ProjectType;
  viewingPartnerName?: string;
}

function getAssignedToDisplay(
  document: SecureProjectDocument,
  allSubprojectsLabel: string,
  viewingProjectType?: ProjectType,
  viewingOrgName?: string,
  viewingPartnerName?: string,
): {
  showAssignedTo: boolean;
  displayName: string | null;
} {
  // Internal/Private -> no Assigned to
  if (document.visibility === 'internal' || document.visibility === 'private') {
    return { showAssignedTo: false, displayName: null };
  }

  // When viewing from Contractor Subproject
  if (viewingProjectType === 'subproject_contractor') {
    // Document from Client (Main/Client Subproject) -> show own Contractor Company Name
    if (
      document.project_type === 'project' ||
      document.project_type === 'subproject_client'
    ) {
      return { showAssignedTo: true, displayName: viewingOrgName ?? null };
    }
    // Self-uploaded document in Contractor Subproject -> show Client Org Name
    if (document.project_type === 'subproject_contractor') {
      return { showAssignedTo: true, displayName: document.client_org_name };
    }
  }

  // When viewing from Client Subproject and document is from Main Project -> show Partner Name
  if (
    viewingProjectType === 'subproject_client' &&
    document.project_type === 'project'
  ) {
    return {
      showAssignedTo: true,
      displayName: viewingPartnerName ?? allSubprojectsLabel,
    };
  }

  // When viewing from Client perspective (Main Project)
  // Public documents
  if (document.project_type === 'project') {
    // Main Project -> "Alle Subprojekte"
    return { showAssignedTo: true, displayName: allSubprojectsLabel };
  }

  if (document.project_type === 'subproject_client') {
    // Client Subproject -> Contractor Name
    return { showAssignedTo: true, displayName: document.partner_names };
  }

  if (document.project_type === 'subproject_contractor') {
    // Contractor Subproject document viewed from Client -> Client Org Name
    return { showAssignedTo: true, displayName: document.client_org_name };
  }

  return { showAssignedTo: false, displayName: null };
}

function getUploadedByDisplay(
  document: SecureProjectDocument,
  viewingProjectType?: ProjectType,
): string {
  // In Contractor project, when viewing a public document from Client project/subproject,
  // show organization name instead of uploader name
  const isViewingFromContractor =
    viewingProjectType === 'subproject_contractor';
  const isDocumentFromClient =
    document.project_type === 'project' ||
    document.project_type === 'subproject_client';
  const isPublicDocument = document.visibility === 'public';

  if (isViewingFromContractor && isDocumentFromClient && isPublicDocument) {
    return document.uploaded_by_org ?? document.creator.name;
  }

  return document.creator.name;
}

const DocumentCard = ({
  document,
  onEdit,
  onDelete,
  hasWriteAccess,
  viewingProjectType,
  viewingPartnerName,
}: DocumentCardProps) => {
  const { t } = useTranslation('document');
  const { t: tShared } = useTranslation('shared');
  const { mutate: downloadDocument } = useDownloadDocument();
  const { data: currentOrg } = useCurrentOrg();
  const {
    data: previewUrl,
    isLoading: isLoadingPreview,
    isError: isPreviewError,
  } = useDocumentPreviewUrl(document.file_path);

  const uploadedByName = getUploadedByDisplay(document, viewingProjectType);
  const creatorAvatarText = getInitials(uploadedByName);
  const documentType = document.type as FileType;

  const { showAssignedTo, displayName } = getAssignedToDisplay(
    document,
    t('all_subprojects'),
    viewingProjectType,
    currentOrg?.name ?? undefined,
    viewingPartnerName,
  );
  const assignedToAvatarText = getInitials(displayName ?? '');

  const handleOpenPreview = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    } else {
      toast({
        title: t('Errors.preview_error' as any),
        description: 'Failed to open preview',
      });
    }
  };

  return (
    <Card
      variant="information"
      className="h-[260px] w-[400px] shrink-0"
      onClick={handleOpenPreview}
    >
      <CardHeader className="pb-12">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-sm">{document.title}</CardTitle>
            <CardDescription className="truncate">
              {document.type}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <OneItemHighlighter
              title={formatDate({
                date: new Date(document.created_at),
                formatType: 'digits',
              })}
              subtitle={t('uploaded_at')}
              icon={
                <VisbilityIndicator
                  visibility={document.visibility as DocumentVisibility}
                />
              }
            />
            <RoundedDropdownMenu
              items={[
                <DropdownMenuItem
                  key="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  {tShared('edit')}
                </DropdownMenuItem>,
                hasWriteAccess && (
                  <DropdownMenuItem
                    key="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  >
                    {tShared('delete')}
                  </DropdownMenuItem>
                ),
                <DropdownMenuItem
                  key="download"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadDocument(document.file_path);
                  }}
                >
                  {t('DocumentHub.download')}
                </DropdownMenuItem>,
              ]}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <div className="flex h-full items-start justify-between gap-4">
          <div
            className={`flex h-full min-w-0 flex-col gap-6 ${!showAssignedTo ? 'justify-end' : ''}`}
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Avatar size="sm" className="shrink-0">
                  <AvatarFallback className="bg-black text-[0.625rem] text-white">
                    {creatorAvatarText}
                  </AvatarFallback>
                </Avatar>
                <CardTitle className="truncate text-2xs">
                  {uploadedByName}
                </CardTitle>
              </div>
              <CardDescription className="truncate text-2xs">
                {t('uploaded_by')}
              </CardDescription>
            </div>
            {showAssignedTo && displayName && (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <Avatar size="sm" className="shrink-0">
                    <AvatarFallback className="bg-black text-[0.625rem] text-white">
                      {assignedToAvatarText}
                    </AvatarFallback>
                  </Avatar>
                  <CardTitle className="truncate text-2xs">
                    {displayName}
                  </CardTitle>
                </div>
                <CardDescription className="truncate text-2xs">
                  {t('assigned_to')}
                </CardDescription>
              </div>
            )}
          </div>
          <div className="relative flex h-[106px] w-[214px] shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-custom-medium-grey bg-black/20">
            {isLoadingPreview || isPreviewError || !previewUrl ? (
              <FileText className="h-8 w-8 text-white/70" />
            ) : documentType === 'image' ? (
              <img
                src={previewUrl}
                alt={document.title}
                className="h-full w-full object-cover"
              />
            ) : documentType === 'pdf' ? (
              <embed
                src={previewUrl}
                type="application/pdf"
                className="h-full w-full"
              />
            ) : (
              <FileText className="h-8 w-8 text-white/70" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentCard;
